import { lazy, Suspense, type ReactElement } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import Layout from '../components/Layout';
import LoadingOverlay from '../components/shared/LoadingOverlay';
import type { TranslationKey } from '../i18n';

const UseMediaList = lazy(() => import('../pages/UseMedia/List'));
const UseMediaCreate = lazy(() => import('../pages/UseMedia/Create'));
const UseMediaUpdate = lazy(() => import('../pages/UseMedia/Update'));
const TemplateList = lazy(() => import('../pages/Template/List'));
const TemplateMailForm = lazy(() => import('../pages/Template/MailForm'));
const TemplateSmsForm = lazy(() => import('../pages/Template/SmsForm'));
const ErrorPage = lazy(() => import('../pages/Error'));
const NotFoundPage = lazy(() => import('../pages/NotFound'));
const Dashboard = lazy(() => import('../pages/Dashboard'));

type RouteHandle = { titleKey: TranslationKey };

const withSuspense = (element: ReactElement): ReactElement => (
  <Suspense fallback={<LoadingOverlay />}>{element}</Suspense>
);

/**
 * Route table — mirrors URLs that existed in the original SSR application.
 * All routes render inside <Layout /> (sidebar + header shell).
 *
 * TEMPORARY: Home path (/) is redirected to /use-media.
 * To restore: Replace the redirect with { index: true, element: <Home /> }
 * and uncomment the home NavLink in src/components/Layout/SideMenu.tsx
 */
export const router = createBrowserRouter([
  {
    path: '/',
    element: <Layout />,
    children: [
      { index: true, element: <Navigate to="/use-media" replace /> },
      {
        path: 'dashboard',
        element: withSuspense(<Dashboard />),
        handle: { titleKey: 'dashboard_report_title' } satisfies RouteHandle,
      },
      {
        path: 'use-media',
        element: withSuspense(<UseMediaList />),
        handle: { titleKey: 'nav_use_media' } satisfies RouteHandle,
      },
      {
        path: 'use-media/create',
        element: withSuspense(<UseMediaCreate />),
        handle: { titleKey: 'use_media_create_title' } satisfies RouteHandle,
      },
      {
        path: 'use-media/:id',
        element: withSuspense(<UseMediaUpdate />),
        handle: { titleKey: 'use_media_update_title' } satisfies RouteHandle,
      },
      {
        path: 'template',
        element: withSuspense(<TemplateList />),
        handle: { titleKey: 'template_page_title' } satisfies RouteHandle,
      },
      {
        path: 'template/mail/create',
        element: withSuspense(<TemplateMailForm />),
        handle: { titleKey: 'template_mail_create_title' } satisfies RouteHandle,
      },
      {
        path: 'template/mail/:id',
        element: withSuspense(<TemplateMailForm />),
        handle: { titleKey: 'template_mail_update_title' } satisfies RouteHandle,
      },
      {
        path: 'template/sms/create',
        element: withSuspense(<TemplateSmsForm />),
        handle: { titleKey: 'template_sms_create_title' } satisfies RouteHandle,
      },
      {
        path: 'template/sms/:id',
        element: withSuspense(<TemplateSmsForm />),
        handle: { titleKey: 'template_sms_update_title' } satisfies RouteHandle,
      },
      {
        path: 'error',
        element: withSuspense(<ErrorPage />),
        handle: { titleKey: 'error_title' } satisfies RouteHandle,
      },
      {
        path: 'not-found',
        element: withSuspense(<NotFoundPage />),
        handle: { titleKey: 'not_found_title' } satisfies RouteHandle,
      },
    ],
  },
  // Catch-all: any unknown URL redirects to the not-found page
  { path: '*', element: <Navigate to="/not-found" replace /> },
]);
